import { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import api from "../api/api";
import { setUser } from "../redux/userReducer";

const BLOOD_GROUP_OPTIONS = [
  "A+",
  "A-",
  "A1+",
  "A1-",
  "A2+",
  "A2-",
  "B+",
  "B-",
  "AB+",
  "AB-",
  "A1B+",
  "A1B-",
  "A2B+",
  "A2B-",
  "O+",
  "O-",
  "Bombay Blood Group",
  "Rh-null",
];
const YES_NO_OPTIONS = ["Yes", "No"];
const PROGRAM_TYPE_OPTIONS = [
  { label: "UG", value: "UG", apiValue: "UG" },
  { label: "PG", value: "PG", apiValue: "PG" },
];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - 2008 + 1 },
  (_, index) => String(CURRENT_YEAR - index),
);

function pickCurrentUserValue(currentUser, keys, fallback = "") {
  for (const key of keys) {
    if (currentUser?.[key] !== undefined && currentUser?.[key] !== null) {
      return currentUser[key];
    }
  }

  return fallback;
}

function toYesNo(value) {
  if (value === 1 || value === "1" || value === true || value === "Yes") {
    return "Yes";
  }

  if (value === 0 || value === "0" || value === false || value === "No") {
    return "No";
  }

  return value || "";
}

function toNullableBoolean(value) {
  if (value === "Yes") {
    return true;
  }

  if (value === "No") {
    return false;
  }

  return null;
}

function toNullableIntegerFlag(value) {
  if (value === "Yes") {
    return 1;
  }

  if (value === "No") {
    return 0;
  }

  return null;
}

function normalizeRole(value) {
  if (!value) {
    return "";
  }

  const lowered = value.toString().trim().toLowerCase();

  if (lowered === "admin") {
    return "Admin";
  }

  if (lowered === "user") {
    return "User";
  }

  return value;
}

function buildProfileForm(currentUser) {
  return {
    firstName: pickCurrentUserValue(currentUser, ["firstName", "FirstName"]),
    lastName: pickCurrentUserValue(currentUser, ["lastName", "LastName"]),
    email: pickCurrentUserValue(currentUser, ["email", "Email"]),
    mobileNumber: pickCurrentUserValue(currentUser, [
      "mobileNumber",
      "MobileNumber",
    ]),
    programType: pickCurrentUserValue(currentUser, [
      "programType",
      "ProgramType",
    ]),
    passedOutYear: String(
      pickCurrentUserValue(currentUser, [
        "passedOutYear",
        "PassedOutYear",
        "yearOfPassedOut",
        "YearOfPassedOut",
      ]),
    ),
    department: pickCurrentUserValue(currentUser, ["department", "Department"]),
    currentJob: pickCurrentUserValue(currentUser, ["currentJob", "CurrentJob"]),
    companyName: pickCurrentUserValue(currentUser, [
      "companyName",
      "CompanyName",
    ]),
    bloodGroup: pickCurrentUserValue(currentUser, ["bloodGroup", "BloodGroup"]),
    interestToDonateBlood: toYesNo(
      pickCurrentUserValue(currentUser, [
        "interestToDonateBlood",
        "IntrestToDonateBlood",
      ]),
    ),
    careerGuidanceVolunteer: toYesNo(
      pickCurrentUserValue(currentUser, [
        "careerGuidanceVolunteer",
        "CareerGuidanceVolunteer",
      ]),
    ),
    volunteerAlumniActivities: toYesNo(
      pickCurrentUserValue(currentUser, [
        "volunteerAlumniActivities",
        "VolunteerAlumniActivities",
      ]),
    ),
    city: pickCurrentUserValue(currentUser, ["city", "City"]),
    state: pickCurrentUserValue(currentUser, ["state", "State"]),
    country: pickCurrentUserValue(currentUser, ["country", "Country"]),
    role: normalizeRole(
      pickCurrentUserValue(currentUser, ["role", "Role"], "User"),
    ),
  };
}

function buildSessionUser(currentUser, apiUser, form) {
  const nextUser = apiUser || {};
  const firstName =
    nextUser.FirstName ?? nextUser.firstName ?? form.firstName.trim();
  const lastName =
    nextUser.LastName ?? nextUser.lastName ?? form.lastName.trim();
  const email = nextUser.Email ?? nextUser.email ?? form.email.trim();
  const mobileNumber =
    nextUser.MobileNumber ?? nextUser.mobileNumber ?? form.mobileNumber.trim();
  const programType =
    nextUser.ProgramType ?? nextUser.programType ?? form.programType.trim();
  const yearOfPassedOut =
    nextUser.YearOfPassedOut ??
    nextUser.yearOfPassedOut ??
    nextUser.PassedOutYear ??
    nextUser.passedOutYear ??
    form.passedOutYear.trim();
  const department =
    nextUser.Department ?? nextUser.department ?? form.department.trim();
  const currentJob =
    nextUser.CurrentJob ?? nextUser.currentJob ?? form.currentJob.trim();
  const companyName =
    nextUser.CompanyName ?? nextUser.companyName ?? form.companyName.trim();
  const bloodGroup =
    nextUser.BloodGroup ?? nextUser.bloodGroup ?? form.bloodGroup.trim();
  const interestToDonateBlood = toYesNo(
    nextUser.IntrestToDonateBlood ??
      nextUser.interestToDonateBlood ??
      form.interestToDonateBlood,
  );
  const careerGuidanceVolunteer = toYesNo(
    nextUser.CareerGuidanceVolunteer ??
      nextUser.careerGuidanceVolunteer ??
      form.careerGuidanceVolunteer,
  );
  const volunteerAlumniActivities = toYesNo(
    nextUser.VolunteerAlumniActivities ??
      nextUser.volunteerAlumniActivities ??
      form.volunteerAlumniActivities,
  );
  const city = nextUser.City ?? nextUser.city ?? form.city.trim();
  const state = nextUser.State ?? nextUser.state ?? form.state.trim();
  const country = nextUser.Country ?? nextUser.country ?? form.country.trim();
  const role = normalizeRole(
    nextUser.Role ?? nextUser.role ?? form.role.trim(),
  );

  return {
    ...currentUser,
    ...nextUser,
    firstName,
    FirstName: firstName,
    lastName,
    LastName: lastName,
    email,
    Email: email,
    mobileNumber,
    MobileNumber: mobileNumber,
    programType,
    ProgramType: programType,
    passedOutYear: String(yearOfPassedOut || ""),
    PassedOutYear: String(yearOfPassedOut || ""),
    yearOfPassedOut: String(yearOfPassedOut || ""),
    YearOfPassedOut: String(yearOfPassedOut || ""),
    department,
    Department: department,
    currentJob,
    CurrentJob: currentJob,
    companyName,
    CompanyName: companyName,
    bloodGroup,
    BloodGroup: bloodGroup,
    interestToDonateBlood,
    IntrestToDonateBlood: interestToDonateBlood,
    careerGuidanceVolunteer,
    CareerGuidanceVolunteer: careerGuidanceVolunteer,
    volunteerAlumniActivities,
    VolunteerAlumniActivities: volunteerAlumniActivities,
    city,
    City: city,
    state,
    State: state,
    country,
    Country: country,
    role,
    Role: role,
    name: `${firstName} ${lastName}`.trim(),
  };
}

function buildProfileUpdatePayload(form, originalForm) {
  const nextPayload = {};
  const fieldMappings = [
    [
      "FirstName",
      form.firstName.trim() || null,
      originalForm.firstName.trim() || null,
    ],
    [
      "LastName",
      form.lastName.trim() || null,
      originalForm.lastName.trim() || null,
    ],
    [
      "MobileNumber",
      form.mobileNumber.trim() || null,
      originalForm.mobileNumber.trim() || null,
    ],
    ["Email", form.email.trim() || null, originalForm.email.trim() || null],
    [
      "YearOfPassedOut",
      form.passedOutYear.trim() ? Number(form.passedOutYear.trim()) : null,
      originalForm.passedOutYear.trim()
        ? Number(originalForm.passedOutYear.trim())
        : null,
    ],
    [
      "ProgramType",
      form.programType.trim() || null,
      originalForm.programType.trim() || null,
    ],
    [
      "Department",
      form.department.trim() || null,
      originalForm.department.trim() || null,
    ],
    [
      "CurrentJob",
      form.currentJob.trim() || null,
      originalForm.currentJob.trim() || null,
    ],
    [
      "CompanyName",
      form.companyName.trim() || null,
      originalForm.companyName.trim() || null,
    ],
    [
      "BloodGroup",
      form.bloodGroup.trim() || null,
      originalForm.bloodGroup.trim() || null,
    ],
    [
      "IntrestToDonateBlood",
      toNullableBoolean(form.interestToDonateBlood),
      toNullableBoolean(originalForm.interestToDonateBlood),
    ],
    [
      "CareerGuidanceVolunteer",
      toNullableIntegerFlag(form.careerGuidanceVolunteer),
      toNullableIntegerFlag(originalForm.careerGuidanceVolunteer),
    ],
    [
      "VolunteerAlumniActivities",
      toNullableBoolean(form.volunteerAlumniActivities),
      toNullableBoolean(originalForm.volunteerAlumniActivities),
    ],
    ["City", form.city.trim() || null, originalForm.city.trim() || null],
    ["State", form.state.trim() || null, originalForm.state.trim() || null],
    [
      "Country",
      form.country.trim() || null,
      originalForm.country.trim() || null,
    ],
    [
      "Role",
      normalizeRole(form.role.trim()) || null,
      normalizeRole(originalForm.role.trim()) || null,
    ],
  ];

  fieldMappings.forEach(([apiKey, nextValue, originalValue]) => {
    if (nextValue !== originalValue) {
      nextPayload[apiKey] = nextValue;
    }
  });

  return nextPayload;
}

function ViewField({ label, value }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid rgba(148, 163, 184, 0.2)",
        bgcolor: "#fff",
        minHeight: 86,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.8,
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600} color="#0f172a">
        {value || "-"}
      </Typography>
    </Box>
  );
}

function Profile() {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.user) || {};
  const [departments, setDepartments] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [form, setForm] = useState(() => buildProfileForm(currentUser));

  useEffect(() => {
    setForm(buildProfileForm(currentUser));
  }, [currentUser]);

  useEffect(() => {
    let isMounted = true;

    async function loadDepartments() {
      try {
        setIsLoadingDepartments(true);
        const response = await api.getDepartments();
        const data = response?.data || response || {};

        if (isMounted) {
          setDepartments(data?.departments || []);
        }
      } catch (error) {
        if (isMounted) {
          setDepartments([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingDepartments(false);
        }
      }
    }

    loadDepartments();

    return () => {
      isMounted = false;
    };
  }, []);

  const fullName =
    `${form.firstName || ""} ${form.lastName || ""}`.trim() ||
    currentUser.name ||
    "Alumni Member";
  const profileInitial = fullName.charAt(0).toUpperCase() || "A";
  const alumniId =
    currentUser.userId ||
    currentUser.id ||
    currentUser.Id ||
    currentUser.UserId;
  const currentUserToken =
    currentUser.token || currentUser.accessToken || currentUser.jwtToken || "";

  const selectedProgramType = PROGRAM_TYPE_OPTIONS.find(
    (option) => option.value === form.programType,
  );
  const availableDepartments = departments.filter(
    (department) => department?.ProgramType === selectedProgramType?.apiValue,
  );

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setErrorMessage("");
    setSaveMessage("");
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "programType" ? { department: "" } : {}),
    }));
  };

  const handleStartEdit = () => {
    setSaveMessage("");
    setErrorMessage("");
    setForm(buildProfileForm(currentUser));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setForm(buildProfileForm(currentUser));
    setSaveMessage("");
    setErrorMessage("");
    setIsEditing(false);
  };

  const handleSaveProfile = async () => {
    setSaveMessage("");
    setErrorMessage("");

    if (!currentUserToken) {
      setErrorMessage(
        "Logged-in session token is missing. Please sign in again.",
      );
      return;
    }

    const originalForm = buildProfileForm(currentUser);
    const payload = buildProfileUpdatePayload(form, originalForm);

    if (Object.keys(payload).length === 0) {
      setIsEditing(false);
      setSaveMessage("No profile changes to save.");
      return;
    }

    try {
      setIsSaving(true);
      const response = await api.updateProfile(payload, currentUserToken);
      const responseData = response?.data || {};
      const updatedUser = buildSessionUser(
        currentUser,
        responseData.user,
        form,
      );

      dispatch(setUser(updatedUser));
      setIsEditing(false);
      setSaveMessage(responseData.message || "Profile updated successfully.");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Profile update failed.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#f8fbff", minHeight: "100vh", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            border: "1px solid rgba(15, 23, 42, 0.08)",
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    bgcolor: "#1d4ed8",
                    fontSize: "1.75rem",
                    fontWeight: 700,
                  }}
                >
                  {profileInitial}
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
                    {fullName}
                  </Typography>
                  <Typography color="text.secondary">
                    {isEditing
                      ? "Edit your profile details."
                      : "View your account details."}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1}>
                {isEditing ? (
                  <>
                    <Tooltip title="Cancel edit">
                      <IconButton
                        onClick={handleCancelEdit}
                        color="inherit"
                        disabled={isSaving}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="contained"
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <CheckIcon />
                        )
                      }
                      sx={{ textTransform: "none", borderRadius: 999 }}
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </Button>
                  </>
                ) : (
                  <Tooltip title="Edit profile">
                    <IconButton onClick={handleStartEdit} color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>

            {saveMessage ? (
              <Alert severity="success">{saveMessage}</Alert>
            ) : null}
            {errorMessage ? (
              <Alert severity="error">{errorMessage}</Alert>
            ) : null}

            {!isEditing ? (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                    Personal details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField label="First name" value={form.firstName} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField label="Last name" value={form.lastName} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField label="Alumni ID" value={alumniId} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField label="Email" value={form.email} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField
                        label="Mobile number"
                        value={form.mobileNumber}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField
                        label="Year of passed out"
                        value={form.passedOutYear}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField
                        label="Program type"
                        value={form.programType}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField label="Department" value={form.department} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField label="Role" value={form.role} />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                    Professional & community details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField label="Current job" value={form.currentJob} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField
                        label="Company name"
                        value={form.companyName}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField label="Blood group" value={form.bloodGroup} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField
                        label="Interested to donate blood"
                        value={form.interestToDonateBlood}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField
                        label="Career guidance volunteer"
                        value={form.careerGuidanceVolunteer}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <ViewField
                        label="Volunteer alumni activities"
                        value={form.volunteerAlumniActivities}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                    Location details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <ViewField label="City" value={form.city} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <ViewField label="State" value={form.state} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <ViewField label="Country" value={form.country} />
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Typography variant="h6" fontWeight={600}>
                  Personal details
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="First name"
                      value={form.firstName}
                      onChange={handleChange("firstName")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Last name"
                      value={form.lastName}
                      onChange={handleChange("lastName")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Mobile number"
                      value={form.mobileNumber}
                      onChange={handleChange("mobileNumber")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={handleChange("email")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Year of passed out"
                      select
                      value={form.passedOutYear}
                      onChange={handleChange("passedOutYear")}
                    >
                      {YEAR_OPTIONS.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Program type"
                      select
                      value={form.programType}
                      onChange={handleChange("programType")}
                    >
                      {PROGRAM_TYPE_OPTIONS.map((programType) => (
                        <MenuItem
                          key={programType.value}
                          value={programType.value}
                        >
                          {programType.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Department"
                      select
                      value={form.department}
                      onChange={handleChange("department")}
                      disabled={
                        isLoadingDepartments ||
                        availableDepartments.length === 0
                      }
                    >
                      {availableDepartments.map((department) => (
                        <MenuItem
                          key={department.DepartmentId}
                          value={department.DepartmentName}
                        >
                          {department.DepartmentName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Role"
                      select
                      value={form.role}
                      onChange={handleChange("role")}
                    >
                      <MenuItem value="User">User</MenuItem>
                      <MenuItem value="Admin">Admin</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                <Divider />
                <Typography variant="h6" fontWeight={600}>
                  Professional & community details
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Current job"
                      value={form.currentJob}
                      onChange={handleChange("currentJob")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Company name"
                      value={form.companyName}
                      onChange={handleChange("companyName")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Blood group"
                      select
                      value={form.bloodGroup}
                      onChange={handleChange("bloodGroup")}
                    >
                      {BLOOD_GROUP_OPTIONS.map((bloodGroup) => (
                        <MenuItem key={bloodGroup} value={bloodGroup}>
                          {bloodGroup}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Interested to donate blood"
                      select
                      value={form.interestToDonateBlood}
                      onChange={handleChange("interestToDonateBlood")}
                    >
                      {YES_NO_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Career guidance volunteer"
                      select
                      value={form.careerGuidanceVolunteer}
                      onChange={handleChange("careerGuidanceVolunteer")}
                    >
                      {YES_NO_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Volunteer alumni activities"
                      select
                      value={form.volunteerAlumniActivities}
                      onChange={handleChange("volunteerAlumniActivities")}
                    >
                      {YES_NO_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                <Divider />
                <Typography variant="h6" fontWeight={600}>
                  Location details
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="City"
                      value={form.city}
                      onChange={handleChange("city")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="State"
                      value={form.state}
                      onChange={handleChange("state")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={form.country}
                      onChange={handleChange("country")}
                    />
                  </Grid>
                </Grid>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default Profile;
